import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  FaBed,
  FaPlane,
  FaChevronDown,
  FaExternalLinkAlt,
  FaTicketAlt,
  FaTaxi,
  FaCar,
} from 'react-icons/fa';
import {
  getHotelBookingLinks,
  getFlightBookingLinks,
  getAttractionBookingLinks,
  getTransferBookingLinks,
  getCarBookingLinks,
  trackAffiliateClick,
} from '../api/client';
import { getAnonSessionId } from '../utils/tripContext';
import { trackBookingClick } from './RetargetingPixels';

const PARTNER_COLORS = {
  booking: 'bg-blue-600 hover:bg-blue-700 text-white',
  expedia: 'bg-yellow-500 hover:bg-yellow-600 text-gray-900',
  kayak: 'bg-orange-500 hover:bg-orange-600 text-white',
  skyscanner: 'bg-sky-500 hover:bg-sky-600 text-white',
  tripadvisor: 'bg-green-600 hover:bg-green-700 text-white',
  google_flights: 'bg-blue-500 hover:bg-blue-600 text-white',
  google_hotels: 'bg-blue-500 hover:bg-blue-600 text-white',
};

/**
 * Everything that varies by product, in one place. The same CJ evergreen link
 * monetises all five, so adding a product is a config entry rather than another
 * branch through the render path.
 *
 * `ready` decides whether there is enough context to search at all; `fetch`
 * calls the matching endpoint; `fallback` is the plain (uncommissioned) search
 * used only when the API itself is unreachable, so a button is never dead.
 */
const TYPE_CONFIG = {
  hotel: {
    Icon: FaBed,
    label: 'Book Hotel',
    preferred: 'booking',
    ready: (p) => !!(p.hotelName || p.city),
    fetch: (p) => getHotelBookingLinks(p.hotelName, p.city, p.checkIn, p.checkOut, p.surface),
    fallback: (p) =>
      `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(
        [p.hotelName, p.city].filter(Boolean).join(' ')
      )}`,
  },
  flight: {
    Icon: FaPlane,
    label: 'Search Flights',
    preferred: 'google_flights',
    ready: (p) => !!(p.origin && p.destination),
    fetch: (p) =>
      getFlightBookingLinks(p.origin, p.destination, p.departDate, p.returnDate, p.surface),
    fallback: (p) =>
      `https://www.google.com/travel/flights?q=flights+from+${encodeURIComponent(
        p.origin || ''
      )}+to+${encodeURIComponent(p.destination || '')}`,
  },
  // "Do I need to book this ahead?" is the most common planning anxiety there
  // is, and it has never had an answer on the attraction card itself.
  attraction: {
    Icon: FaTicketAlt,
    label: 'Tickets',
    preferred: 'booking',
    ready: (p) => !!(p.attractionName || p.city),
    fetch: (p) =>
      getAttractionBookingLinks(p.attractionName, p.city, p.checkIn, p.surface),
    fallback: (p) =>
      `https://www.booking.com/attractions/index.html?ss=${encodeURIComponent(
        [p.attractionName, p.city].filter(Boolean).join(' ')
      )}`,
  },
  transfer: {
    Icon: FaTaxi,
    label: 'Airport transfer',
    preferred: 'booking',
    ready: (p) => !!p.city,
    fetch: (p) => getTransferBookingLinks(p.city, p.checkIn, p.surface),
    fallback: (p) =>
      `https://www.booking.com/taxi/index.html?ss=${encodeURIComponent(p.city || '')}`,
  },
  car: {
    Icon: FaCar,
    label: 'Car hire',
    preferred: 'booking',
    ready: (p) => !!p.city,
    fetch: (p) => getCarBookingLinks(p.city, p.checkIn, p.checkOut, p.surface),
    fallback: (p) =>
      `https://www.booking.com/cars/index.html?ss=${encodeURIComponent(p.city || '')}`,
  },
};

const BookingOptions = ({
  type = 'hotel', // 'hotel' | 'flight' | 'attraction' | 'transfer' | 'car'
  hotelName,
  attractionName,
  city,
  checkIn,
  checkOut,
  origin,
  destination,
  departDate,
  returnDate,
  compact = false,
  // Per-surface CJ sub-ID — itin_{requestId}_day{n} inside a real trip,
  // sample_{slug}_day{n} on the public samples. Without it every click on the
  // site reports under one of two values and no surface can be evaluated.
  surface,
}) => {
  const [links, setLinks] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  // Fixed-position box for the menu, measured from the trigger. Null until the
  // first measurement, which keeps the menu invisible for that one frame.
  const [menuPos, setMenuPos] = useState(null);

  // Close dropdown on outside click. The menu is portalled to <body>, so it is
  // outside dropdownRef and has to be excluded explicitly — otherwise mousedown
  // on a partner would close the menu before its click ever fired.
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // The menu renders in a portal at fixed coordinates instead of absolutely
  // inside the trigger: the day card that hosts the Airport transfer / Car hire
  // buttons is overflow-hidden, which clipped the menu to the card. It opens
  // upwards whenever there is room, since these buttons sit near the bottom of
  // the card where a downward menu runs past the fold.
  const placeMenu = useCallback(() => {
    const trigger = dropdownRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = compact ? 192 : 208; // w-48 / w-52
    const gap = 4; // mt-1
    const margin = 8;
    // scrollHeight, not offsetHeight: once a menu has been clamped by maxHeight
    // the offset height is the clamped one, and it could never grow back.
    const menuHeight = menuRef.current ? menuRef.current.scrollHeight + 2 : 0;
    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const openUp = menuHeight === 0
      ? spaceAbove >= spaceBelow
      : spaceAbove >= menuHeight + gap || spaceAbove >= spaceBelow;
    const available = Math.max(120, (openUp ? spaceAbove : spaceBelow) - gap);
    const height = menuHeight ? Math.min(menuHeight, available) : available;
    const top = openUp ? rect.top - gap - height : rect.bottom + gap;
    // Left-aligned to the trigger. Right-aligning it (the old behaviour) hung
    // the menu off the left of the day card, because these triggers — Airport
    // transfer especially — are narrower than the menu. Only flip to
    // right-aligned when left-aligning would run off the right of the screen.
    let left = rect.left;
    if (left + width > window.innerWidth - margin) {
      left = rect.right - width;
    }
    left = Math.min(
      Math.max(margin, left),
      Math.max(margin, window.innerWidth - width - margin)
    );
    setMenuPos({ top, left, width, maxHeight: available });
  }, [compact]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return undefined;
    }
    placeMenu();
    // `true` so the menu follows scrolling inside the itinerary panes too, not
    // just the window.
    window.addEventListener('scroll', placeMenu, true);
    window.addEventListener('resize', placeMenu);
    return () => {
      window.removeEventListener('scroll', placeMenu, true);
      window.removeEventListener('resize', placeMenu);
    };
  }, [isOpen, links, placeMenu]);

  const menuStyle = {
    position: 'fixed',
    top: menuPos ? menuPos.top : 0,
    left: menuPos ? menuPos.left : 0,
    width: menuPos ? menuPos.width : compact ? 192 : 208,
    maxHeight: menuPos ? menuPos.maxHeight : undefined,
    overflowY: 'auto',
    zIndex: 9999,
    visibility: menuPos ? 'visible' : 'hidden',
  };

  const config = TYPE_CONFIG[type] || TYPE_CONFIG.hotel;
  const params = {
    hotelName,
    attractionName,
    city,
    checkIn,
    checkOut,
    origin,
    destination,
    departDate,
    returnDate,
    surface,
  };

  // Load the links as soon as the component mounts, so the primary button
  // navigates on the first click. Without this the first click only populates
  // the dropdown and relabels the button — which on a phone, where the dropdown
  // can be off-screen, reads as a dead button.
  useEffect(() => {
    if (!config.ready(params)) return;
    let cancelled = false;
    config
      .fetch(params)
      .then((data) => {
        if (!cancelled) setLinks(data);
      })
      .catch(() => {
        // Leave links null — fetchLinks() retries on click and falls back to a
        // plain search if that fails too.
      });
    return () => {
      cancelled = true;
    };
    // params is rebuilt every render; the individual values are the real deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    hotelName,
    attractionName,
    city,
    checkIn,
    checkOut,
    origin,
    destination,
    departDate,
    returnDate,
    surface,
  ]);

  const fetchLinks = async () => {
    if (links) {
      setIsOpen(!isOpen);
      return;
    }
    setLoading(true);
    try {
      setLinks(await config.fetch(params));
      setIsOpen(true);
    } catch {
      // The API is unreachable — open an untracked search rather than nothing.
      window.open(config.fallback(params), '_blank', 'noopener,noreferrer');
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerClick = (partnerKey, partner) => {
    trackAffiliateClick(partnerKey, type, partner.url, {
      hotelName: type === 'attraction' ? attractionName : hotelName,
      city,
      origin,
      destination: type === 'flight' ? destination : city,
      surface,
      subId: surface,
      sessionId: getAnonSessionId(),
      checkIn: type === 'flight' ? departDate : checkIn,
      checkOut: type === 'flight' ? returnDate : checkOut,
    }).catch(() => {});
    // Highest-intent signal the site produces — feed it to the ad platforms so
    // a visitor who doesn't come back can be reached again.
    trackBookingClick(city || destination, type, partnerKey);
    window.open(partner.url, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  const partnerEntries = links ? Object.entries(links) : [];
  // Booking.com is the monetised partner everywhere it is offered; the per-type
  // preference only decides what to promote when it isn't in the returned set.
  const primaryPartner = links && links.booking ? 'booking' : config.preferred;
  const primaryEntry = partnerEntries.find(([k]) => k === primaryPartner);
  const otherEntries = partnerEntries.filter(([k]) => k !== primaryPartner);

  const Icon = config.Icon;
  const primaryLabel = config.label;

  if (compact) {
    return (
      <div className="relative inline-block" ref={dropdownRef}>
        <button
          onClick={fetchLinks}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          <Icon size={12} />
          {loading ? '...' : primaryLabel}
          <FaChevronDown size={8} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && links && ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="bg-white rounded-lg shadow-lg border border-neutral-200 py-1"
          >
            {partnerEntries.map(([key, partner]) => (
              <button
                key={key}
                onClick={() => handlePartnerClick(key, partner)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <FaExternalLinkAlt size={10} className="text-neutral-400" />
                {partner.name}
              </button>
            ))}
            <p className="px-3 pt-2 pb-1 text-[11px] leading-snug text-neutral-400 border-t border-neutral-100 mt-1">
              We may earn a commission from these links, at no extra cost to you.
            </p>
          </div>,
          document.body
        )}
      </div>
    );
  }

  return (
    <div className="relative inline-flex" ref={dropdownRef}>
      {/* Primary button */}
      <button
        onClick={() => {
          if (primaryEntry) {
            handlePartnerClick(primaryEntry[0], primaryEntry[1]);
          } else {
            fetchLinks();
          }
        }}
        disabled={loading}
        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-l-lg transition-colors shadow-sm ${
          PARTNER_COLORS[primaryPartner] || 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        <Icon size={14} />
        {loading ? 'Loading...' : primaryEntry ? primaryEntry[1].name : primaryLabel}
      </button>

      {/* Dropdown toggle */}
      <button
        onClick={fetchLinks}
        disabled={loading}
        className={`inline-flex items-center px-2 py-2 text-sm font-medium rounded-r-lg border-l border-white/30 transition-colors shadow-sm ${
          PARTNER_COLORS[primaryPartner] || 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        <FaChevronDown size={10} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && links && otherEntries.length > 0 && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="bg-white rounded-lg shadow-lg border border-neutral-200 py-1"
        >
          <div className="px-3 py-1.5 text-xs font-medium text-neutral-400 uppercase">
            Also available on
          </div>
          {otherEntries.map(([key, partner]) => (
            <button
              key={key}
              onClick={() => handlePartnerClick(key, partner)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              <FaExternalLinkAlt size={10} className="text-neutral-400" />
              {partner.name}
            </button>
          ))}
          {/* Required by CJ's terms and by advertising rules in the US, UK and
              EU wherever an affiliate link is shown. */}
          <p className="px-3 pt-2 pb-1 text-[11px] leading-snug text-neutral-400 border-t border-neutral-100 mt-1">
            We may earn a commission from these links, at no extra cost to you.
          </p>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BookingOptions;
